import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AyurvedaCard, AyurvedaCardHeader, AyurvedaCardTitle, AyurvedaCardDescription, AyurvedaCardContent } from '@/components/ui/ayurveda-card';
import { AyurvedaButton } from '@/components/ui/ayurveda-button';
import { PageTransition, StaggerContainer, StaggerItem, FadeInUp, FloatingElement } from '@/components/ui/page-transition';
import {
  LotusIcon,
  LeafIcon,
  HerbIcon,
  WellnessDropIcon,
  PulseIcon,
  BalanceIcon
} from '@/components/ui/ayurveda-icons';
import { Star, Sparkles } from 'lucide-react';

export const AyurvedaShowcase = () => {
  const [activeTab, setActiveTab] = useState('components');

  const componentExamples = [
    {
      title: 'Glassmorphic Cards',
      description: 'Beautiful glass-effect cards with backdrop blur',
      component: (
        <AyurvedaCard variant="default" className="max-w-sm">
          <AyurvedaCardHeader>
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 p-2 rounded-lg">
                <LotusIcon className="h-5 w-5 text-white" />
              </div>
              <AyurvedaCardTitle>Wellness Card</AyurvedaCardTitle>
            </div>
          </AyurvedaCardHeader>
          <AyurvedaCardContent>
            <AyurvedaCardDescription>
              This card demonstrates the glassmorphic design with subtle transparency and backdrop blur effects.
            </AyurvedaCardDescription>
          </AyurvedaCardContent>
        </AyurvedaCard>
      )
    },
    {
      title: 'Animated Buttons',
      description: 'Smooth hover animations with Ayurvedic styling',
      component: (
        <div className="flex flex-wrap gap-3">
          <AyurvedaButton variant="primary">Primary Action</AyurvedaButton>
          <AyurvedaButton variant="secondary">Secondary</AyurvedaButton>
          <AyurvedaButton variant="wellness">Wellness</AyurvedaButton>
          <AyurvedaButton variant="outline">Outline</AyurvedaButton>
        </div>
      )
    },
    {
      title: 'Ayurvedic Icons',
      description: 'Custom animated icons with natural themes',
      component: (
        <div className="flex flex-wrap gap-4 sm:gap-6 items-center justify-center">
          <div className="text-ayurveda-sage-600">
            <LotusIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Lotus</p>
          </div>
          <div className="text-ayurveda-herbal-600">
            <LeafIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Leaf</p>
          </div>
          <div className="text-ayurveda-gold-500">
            <HerbIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Herb</p>
          </div>
          <div className="text-ayurveda-sandalwood-500">
            <WellnessDropIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Drop</p>
          </div>
          <div className="text-ayurveda-sage-600">
            <PulseIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Pulse</p>
          </div>
          <div className="text-ayurveda-herbal-600">
            <BalanceIcon className="h-12 w-12" />
            <p className="text-xs mt-2 text-center">Balance</p>
          </div>
        </div>
      )
    }
  ];

  const colorPalette = [
    { name: 'Sage Green', colors: ['#f6f8f6', '#5d7a5d', '#2b362b'], variable: 'ayurveda-sage' },
    { name: 'Herbal Green', colors: ['#f0f9f0', '#3a9b3a', '#1e421e'], variable: 'ayurveda-herbal' },
    { name: 'Beige', colors: ['#faf9f7', '#b19b7a', '#584d3f'], variable: 'ayurveda-beige' },
    { name: 'Sandalwood', colors: ['#faf8f5', '#b3875e', '#5c4132'], variable: 'ayurveda-sandalwood' },
    { name: 'Gold', colors: ['#fefbf3', '#eaab47', '#785023'], variable: 'ayurveda-gold' }
  ];

  const animationExamples = [
    {
      title: 'Breathing Animation',
      description: 'Organic breathing effect for cards',
      component: (
        <AyurvedaCard className="breathe max-w-xs">
          <AyurvedaCardContent className="p-6 text-center">
            <LotusIcon className="h-16 w-16 mx-auto mb-4 text-ayurveda-sage-600" />
            <p className="text-ayurveda-sage-700">This card breathes naturally</p>
          </AyurvedaCardContent>
        </AyurvedaCard>
      )
    },
    {
      title: 'Floating Elements',
      description: 'Gentle floating animation',
      component: (
        <div className="relative h-32 flex items-center justify-center">
          <FloatingElement>
            <div className="bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 p-4 rounded-full shadow-lg">
              <LeafIcon className="h-8 w-8 text-white" />
            </div>
          </FloatingElement>
        </div>
      )
    },
    {
      title: 'Stagger Animation',
      description: 'Sequential reveal animation',
      component: (
        <StaggerContainer className="space-y-3">
          {[1, 2, 3].map((item) => (
            <StaggerItem key={item}>
              <div className="glass-subtle p-4 rounded-lg flex items-center gap-3">
                <div className="bg-ayurveda-gold-100 p-2 rounded-lg">
                  <Star className="h-4 w-4 text-ayurveda-gold-500" />
                </div>
                <span className="text-ayurveda-sage-700">Stagger Item {item}</span>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      )
    }
  ];

  return (
    <PageTransition className="relative overflow-hidden bg-white flex flex-col">
      {/* Header */}
      <div className="glass-card sticky top-0 z-50 border-b border-ayurveda-sage-200 mb-8">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <motion.div
              className="bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 p-3 rounded-xl shadow-lg"
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              <LotusIcon className="h-8 w-8 text-white" />
            </motion.div>
            <div>
              <h1 className="ayurveda-heading text-3xl font-bold text-ayurveda-sage-700">
                Ayurvedic Design System
              </h1>
              <p className="text-ayurveda-sage-600">Showcase of components, colors, and animations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-16">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8">
          {['components', 'colors', 'animations'].map((tab) => (
            <AyurvedaButton
              key={tab}
              variant={activeTab === tab ? 'primary' : 'ghost'}
              onClick={() => setActiveTab(tab)}
              className="capitalize"
            >
              {tab}
            </AyurvedaButton>
          ))}
        </div>

        {/* Components Tab */}
        {activeTab === 'components' && (
          <StaggerContainer className="space-y-12">
            <StaggerItem>
              <FadeInUp>
                <h2 className="ayurveda-heading text-2xl font-bold text-ayurveda-sage mb-6">
                  UI Components
                </h2>
              </FadeInUp>
            </StaggerItem>

            {componentExamples.map((example, index) => (
              <StaggerItem key={index}>
                <AyurvedaCard>
                  <AyurvedaCardHeader>
                    <AyurvedaCardTitle>{example.title}</AyurvedaCardTitle>
                    <AyurvedaCardDescription>{example.description}</AyurvedaCardDescription>
                  </AyurvedaCardHeader>
                  <AyurvedaCardContent>
                    <div className="flex justify-center p-6 bg-ayurveda-sage-50 rounded-lg">
                      {example.component}
                    </div>
                  </AyurvedaCardContent>
                </AyurvedaCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <StaggerContainer className="space-y-8">
            <StaggerItem>
              <FadeInUp>
                <h2 className="ayurveda-heading text-2xl font-bold text-ayurveda-sage mb-6">
                  Ayurvedic Color Palette
                </h2>
              </FadeInUp>
            </StaggerItem>

            {colorPalette.map((palette, index) => (
              <StaggerItem key={index}>
                <AyurvedaCard>
                  <AyurvedaCardHeader>
                    <AyurvedaCardTitle>{palette.name}</AyurvedaCardTitle>
                    <AyurvedaCardDescription>
                      CSS Variable: <code className="bg-ayurveda-sage-100 px-2 py-1 rounded text-sm">
                        --{palette.variable}
                      </code>
                    </AyurvedaCardDescription>
                  </AyurvedaCardHeader>
                  <AyurvedaCardContent>
                    <div className="flex gap-4">
                      {palette.colors.map((color, colorIndex) => (
                        <motion.div
                          key={colorIndex}
                          className="flex-1 text-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <div
                            className="h-20 rounded-lg shadow-lg mb-2"
                            style={{ backgroundColor: color }}
                          />
                          <p className="text-sm font-mono text-ayurveda-sage-600">{color}</p>
                        </motion.div>
                      ))}
                    </div>
                  </AyurvedaCardContent>
                </AyurvedaCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}

        {/* Animations Tab */}
        {activeTab === 'animations' && (
          <StaggerContainer className="space-y-12">
            <StaggerItem>
              <FadeInUp>
                <h2 className="ayurveda-heading text-2xl font-bold text-ayurveda-sage mb-6">
                  Framer Motion Animations
                </h2>
              </FadeInUp>
            </StaggerItem>

            {animationExamples.map((example, index) => (
              <StaggerItem key={index}>
                <AyurvedaCard>
                  <AyurvedaCardHeader>
                    <AyurvedaCardTitle>{example.title}</AyurvedaCardTitle>
                    <AyurvedaCardDescription>{example.description}</AyurvedaCardDescription>
                  </AyurvedaCardHeader>
                  <AyurvedaCardContent>
                    <div className="flex justify-center p-6 bg-ayurveda-sage-50 rounded-lg">
                      {example.component}
                    </div>
                  </AyurvedaCardContent>
                </AyurvedaCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}

        {/* Usage Guidelines */}
        <FadeInUp delay={0.8} className="mt-16">
          <AyurvedaCard variant="solid" className="bg-gradient-to-br from-ayurveda-beige-50 to-ayurveda-sage-50">
            <AyurvedaCardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-ayurveda-gold-500 to-ayurveda-sandalwood-500 p-2 rounded-lg">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <AyurvedaCardTitle>Design Guidelines</AyurvedaCardTitle>
              </div>
            </AyurvedaCardHeader>
            <AyurvedaCardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-ayurveda-sage-700 mb-2">Visual Principles</h4>
                  <ul className="text-sm text-ayurveda-sage-600 space-y-1">
                    <li>• Use glassmorphism for depth and elegance</li>
                    <li>• Apply breathing animations for organic feel</li>
                    <li>• Maintain generous white space</li>
                    <li>• Use rounded corners (12px+) for softness</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-ayurveda-sage-700 mb-2">Motion Guidelines</h4>
                  <ul className="text-sm text-ayurveda-sage-600 space-y-1">
                    <li>• Use easing curves: [0.4, 0, 0.2, 1]</li>
                    <li>• Keep animations subtle and calming</li>
                    <li>• Stagger elements for natural flow</li>
                    <li>• Duration: 0.3-0.6s for interactions</li>
                  </ul>
                </div>
              </div>
            </AyurvedaCardContent>
          </AyurvedaCard>
        </FadeInUp>
      </div>
    </PageTransition>
  );
};

export default AyurvedaShowcase;
